import { db } from "../db/indexeddb";
import axios from "axios";
import { API_ENDPOINT } from "../../services/api/resources";

export class ComparisonRepository {
  static _syncInFlight = new Map();

  _metaKey(prefix, buyerToken) {
    return `${prefix}_${buyerToken || "all"}`;
  }

  _normalizeDateKey(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    return raw;
  }

  _makeRowToken(item) {
    const prn = String(item?.prn_number || item?.prn || item?.pr_number || "").trim();
    const created =
      this._normalizeDateKey(item?.created_date) ||
      this._normalizeDateKey(item?.created_on) ||
      this._normalizeDateKey(item?.createdAt) ||
      this._normalizeDateKey(item?.created_at);
    const fallbackId = String(
      item?.compare_history ||
        item?.comparison_token ||
        item?.token ||
        item?.id ||
        ""
    ).trim();

    // Must be stable; avoid "PRN_undefined" which causes overwrites and endless re-syncs
    return `${prn || "prn"}_${created || fallbackId || "na"}`;
  }

  /* ==============================
     Get From Cache (Offline Mode)
  ============================== */
  async getFromCache(buyerToken, searchQuery = "", pageNumber = 1, pageSize = 10) {

    let data = await db.comparisonHistory.toArray();

    // isolate by buyer (best-effort; older cached rows may not have buyer_token)
    if (buyerToken) {
      data = data.filter((d) => !d?.buyer_token || d?.buyer_token === buyerToken);
    }

    let records = data.map((d) => d.data);

    /* SEARCH */
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      records = records.filter(
        (item) => item.prn_number?.toLowerCase().includes(q)
      );
    }

    /* PAGINATION */
    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;

    return {
      results: records.slice(start, end),
      total_count: records.length,
      page_count: Math.ceil(records.length / pageSize),
    };
  }


  /* ==============================
     Save Page To Cache
  ============================== */
  async savePageToCache(pageData = [], page = 1, buyerToken = null) {
    if (!pageData.length) return;

    const rows = pageData.map((item) => {
      const createdDate =
        item?.created_date ||
        item?.created_on ||
        item?.createdAt ||
        item?.created_at ||
        null;

      return {
        token: this._makeRowToken(item),
        buyer_token: buyerToken || null,
        prn_number: item?.prn_number || item?.prn || item?.pr_number || null,
        created_date: createdDate,
        page,
        data: item,
        lastSynced: Date.now(),
      };
    });

    await db.transaction("rw", db.comparisonHistory, async () => {
      for (const row of rows) {
        await db.comparisonHistory.put(row); // put = no duplicate
      }
    });

    if (buyerToken) {
      const prev = await this.getLastSyncedPage(buyerToken);
      if (page > prev) await this.setLastSyncedPage(buyerToken, page);
    }
  }

  async getCacheCount(buyerToken) {
    const rows = await db.comparisonHistory.toArray();
    if (!buyerToken) return rows.length;
    return rows.filter((r) => !r?.buyer_token || r?.buyer_token === buyerToken).length;
  }

  /* ==============================
     Background Full Sync
  ============================== */
  async syncAllPages(authToken, buyerToken) {

    if (!navigator.onLine) {
      return this.getFromCache(buyerToken, "");
    }

    const status = await this.getSyncStatus(buyerToken);
    if (status.cacheComplete && status.cacheCount > 0) {
      return this.getFromCache(buyerToken, "");
    }

    let page = Math.max(1, (await this.getLastSyncedPage(buyerToken)) || 1);
    let totalPages = 1;
    let totalCount = 0;

    do {

      const formData = new FormData();
      formData.append("buyer_token", buyerToken);
      formData.append("page", page);

      const response = await axios.post(
        API_ENDPOINT.comparison_history,
        formData,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const history = response.data.response || [];
      const pagination = response.data.pagination || {};

      totalPages = Number(pagination.page_count || 1) || 1;
      totalCount = Number(pagination.total_count || 0) || totalCount;

      /* SAVE EACH PAGE */
      await this.savePageToCache(history, page, buyerToken);

      page++;

    } while (page <= totalPages);

    await this.setLastTotalPages(buyerToken, totalPages);
    if (totalCount) await this.setLastTotalCount(buyerToken, totalCount);
    const cacheCount = await this.getCacheCount(buyerToken);
    await this.setCacheComplete(buyerToken, totalCount ? cacheCount >= totalCount : cacheCount > 0);
    await this.setLastSyncAt(buyerToken, Date.now());

    return this.getFromCache(buyerToken, "");
  }

  async fetchPageFromAPI(authToken, buyerToken, page = 1, searchQuery = "", options = {}) {
    const { signal } = options || {};
    const formData = new FormData();
    formData.append("buyer_token", buyerToken);
    formData.append("page", page);
    if (searchQuery) formData.append("search", searchQuery);

    const response = await axios.post(
      API_ENDPOINT.comparison_history,
      formData,
      { headers: { Authorization: `Bearer ${authToken}` }, signal }
    );

    const history = response.data?.response || [];
    const pagination = response.data?.pagination || {};

    return {
      history,
      pagination: {
        page_count: Number(pagination.page_count || 1) || 1,
        total_count: Number(pagination.total_count || 0) || 0,
        current_page_count: Number(pagination.current_page_count || history.length || 0) || 0,
      },
    };
  }

  async syncRemainingPagesInBackground(
    authToken,
    buyerToken,
    pageCount,
    currentPage,
    options = {}
  ) {
    const { force = false, expectedTotal = 0 } = options || {};
    const normalizedPageCount = Number(pageCount || 0) || 0;
    if (!buyerToken || !navigator.onLine || normalizedPageCount < 2) return;

    const syncKey = this._metaKey("comparison_sync", buyerToken);
    if (ComparisonRepository._syncInFlight.get(syncKey)) {
      console.log("🔁 Comparison background sync already in-flight — skipping");
      return;
    }

    const status = await this.getSyncStatus(buyerToken);
    if (!force && status.cacheComplete && status.cacheCount > 0) {
      console.log("✅ Comparison cache marked complete — skipping background sync");
      return;
    }

    const cacheEmpty = status.cacheCount === 0;
    const lastSyncedPage = cacheEmpty ? 0 : await this.getLastSyncedPage(buyerToken);
    const lastTotalPages = cacheEmpty ? 0 : await this.getLastTotalPages(buyerToken);
    const lastTotalCount = cacheEmpty ? 0 : await this.getLastTotalCount(buyerToken);

    if (!force) {
      if (expectedTotal && status.cacheCount >= expectedTotal && lastSyncedPage >= normalizedPageCount) return;
      if (expectedTotal && lastTotalCount && expectedTotal <= lastTotalCount && lastSyncedPage >= normalizedPageCount) return;
      if (lastTotalPages && normalizedPageCount <= lastTotalPages && lastSyncedPage >= normalizedPageCount) return;
    }

    let pages = [];
    for (let p = 1; p <= normalizedPageCount; p++) {
      if (p === currentPage) continue;
      if (!force && !cacheEmpty && p <= lastSyncedPage) continue;
      pages.push(p);
    }

    if (force && pages.length === 0) {
      for (let p = 1; p <= normalizedPageCount; p++) if (p !== currentPage) pages.push(p);
    }

    if (pages.length === 0) {
      await this.setLastTotalPages(buyerToken, normalizedPageCount);
      await this.setLastSyncedPage(buyerToken, Math.max(lastSyncedPage, normalizedPageCount));
      if (expectedTotal) await this.setLastTotalCount(buyerToken, expectedTotal);
      const cacheCount = await this.getCacheCount(buyerToken);
      await this.setCacheComplete(buyerToken, expectedTotal ? cacheCount >= expectedTotal : cacheCount > 0);
      await this.setLastSyncAt(buyerToken, Date.now());
      return;
    }

    console.log(
      `🔄 Comparison background sync: ${pages.length} remaining pages (current ${currentPage}) for offline cache`
    );
    ComparisonRepository._syncInFlight.set(syncKey, true);
    try {
      for (const p of pages) {
        try {
          const { history } = await this.fetchPageFromAPI(authToken, buyerToken, p, "");
          await this.savePageToCache(history, p, buyerToken);
          await this.setLastSyncedPage(buyerToken, p);
          await new Promise((r) => setTimeout(r, 250));
        } catch (e) {
          console.warn(`Comparison background sync page ${p} failed:`, e?.message || e);
        }
      }

      await this.setLastTotalPages(buyerToken, normalizedPageCount);
      if (expectedTotal) await this.setLastTotalCount(buyerToken, expectedTotal);
      const cacheCount = await this.getCacheCount(buyerToken);
      await this.setCacheComplete(buyerToken, expectedTotal ? cacheCount >= expectedTotal : cacheCount > 0);
      await this.setLastSyncedPage(buyerToken, Math.max(lastSyncedPage, normalizedPageCount));
      await this.setLastSyncAt(buyerToken, Date.now());
      console.log("✅ Comparison background sync complete");
    } finally {
      ComparisonRepository._syncInFlight.delete(syncKey);
    }
  }

  /* ==============================
     Sync Meta
  ============================== */
  async setLastSyncedPage(buyerToken, page) {
    await db.comparisonSyncMeta.put({
      key: this._metaKey("comparison_last_page", buyerToken),
      value: page,
    });
  }

  async getLastSyncedPage(buyerToken) {
    const res = await db.comparisonSyncMeta.get(this._metaKey("comparison_last_page", buyerToken));
    return res?.value || 0;
  }

  async setLastTotalPages(buyerToken, totalPages) {
    await db.comparisonSyncMeta.put({
      key: this._metaKey("comparison_last_total_pages", buyerToken),
      value: totalPages,
    });
  }

  async getLastTotalPages(buyerToken) {
    const res = await db.comparisonSyncMeta.get(this._metaKey("comparison_last_total_pages", buyerToken));
    return res?.value || 0;
  }

  async setLastTotalCount(buyerToken, totalCount) {
    await db.comparisonSyncMeta.put({
      key: this._metaKey("comparison_last_total_count", buyerToken),
      value: totalCount,
    });
  }

  async getLastTotalCount(buyerToken) {
    const res = await db.comparisonSyncMeta.get(this._metaKey("comparison_last_total_count", buyerToken));
    return res?.value || 0;
  }

  async setCacheComplete(buyerToken, value) {
    await db.comparisonSyncMeta.put({
      key: this._metaKey("comparison_cache_complete", buyerToken),
      value: Boolean(value),
    });
  }

  async getCacheComplete(buyerToken) {
    const res = await db.comparisonSyncMeta.get(this._metaKey("comparison_cache_complete", buyerToken));
    return Boolean(res?.value);
  }

  async setLastSyncAt(buyerToken, timestamp) {
    await db.comparisonSyncMeta.put({
      key: this._metaKey("comparison_last_sync_at", buyerToken),
      value: timestamp,
    });
  }

  async getLastSyncAt(buyerToken) {
    const res = await db.comparisonSyncMeta.get(this._metaKey("comparison_last_sync_at", buyerToken));
    return res?.value || 0;
  }

  async getSyncStatus(buyerToken) {
    const [cacheCount, cacheComplete] = await Promise.all([
      this.getCacheCount(buyerToken),
      this.getCacheComplete(buyerToken),
    ]);
    return { cacheCount, cacheComplete };
  }

}



export const comparisonRepository = new ComparisonRepository();
